import { Injectable } from '@angular/core'
import { environment } from '@employer/environments/environment'
import { E11ErrorHandlerService } from '@engineering11/web-api-error'
import { RestApiClient } from '@engineering11/web-api-rest'

import { catchError, lastValueFrom, map, Observable, of } from 'rxjs'
import { ICJob, ICJobInviteDto, ICJobQuestion, ICJobSurveyResponsesDTO, ISetIdealDTO, ISurveyMatchResult, ITraitDots, TokenStorage } from 'shared-lib'

const CULTURE_INDEX_PATH = '/culture-index/jobs'
@Injectable({ providedIn: 'root' })
export class CultureIndexJobPostsService {
  private restClient: RestApiClient

  constructor(tokenStorage: TokenStorage, private errorHandler: E11ErrorHandlerService) {
    this.restClient = new RestApiClient({
      baseUrl: environment.services.integration,
      token: () => tokenStorage.getAccessToken(),
    })
  }

  getCJobs(contentId: string): Observable<ICJob[] | undefined> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.restClient.get<any[]>(`${CULTURE_INDEX_PATH}/${contentId}`, { version: '1' }).pipe(
      map(item => {
        return item.data.map(v => {
          const parsedTraitDots = JSON.parse(v['traitDots'] as unknown as string)
          return {
            ...v,
            traitDots: parsedTraitDots as ITraitDots,
          } as ICJob
        })
      }),
      catchError(error => {
        this.errorHandler.handleE11Error(error, { alertUser: true })
        return of(undefined)
      })
    )
  }

  getCJobQuestionnaire(): Observable<ICJobQuestion[]> {
    return this.restClient.get<ICJobQuestion[]>(`${CULTURE_INDEX_PATH}/cjobs/questionnaire`, { version: '1' }).pipe(map(item => item.data))
  }

  async createCJob(jobContentId: string, payload: ICJobSurveyResponsesDTO): Promise<void> {
    await this.restClient.post(`${CULTURE_INDEX_PATH}/${jobContentId}/cjobs`, payload, { version: '1' })
  }

  async sendQuestionnaireInvite(jobContentId: string, payload: ICJobInviteDto): Promise<void> {
    await this.restClient.post(`${CULTURE_INDEX_PATH}/${jobContentId}/invite`, payload, { version: '1' })
  }

  async setIdealPattern(jobContentId: string, payload: ISetIdealDTO): Promise<void> {
    await this.restClient.post(`${CULTURE_INDEX_PATH}/${jobContentId}/set-ideal`, payload, { version: '1' })
  }

  requestSurvey(jobContentId: string, applicationId: string) {
    return lastValueFrom(this.restClient.post(`${CULTURE_INDEX_PATH}/${jobContentId}/surveys/${applicationId}`, {}, { version: '1' }))
  }
  getSurveyMatchResult(jobContentId: string, applicationIds: string[]): Observable<Record<string, ISurveyMatchResult>> {
    const appIds = [applicationIds.join(',')]
    return this.restClient.get(`${CULTURE_INDEX_PATH}/${jobContentId}/surveys?applications=${appIds}`, { version: '1' }).pipe(
      map(item => {
        return item.data
      })
    )
  }

  async deactivateCJob(jobContentId: string, cJobId: number): Promise<void> {
    await this.restClient.post(`/culture-index/jobs/${jobContentId}/cjobs/${cJobId}/deactivate`, {}, { version: '1' })
  }
}
